import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, /* FileJson, */ Printer } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { MultilineTextSkeleton } from '@/components/ui/content-loading-skeletons';
import { downloadJson } from '@/lib/downloadJson';

type ExportFormat = 'fhir' | 'phenopacket';

interface ExportMenuProps {
  readonly guid: string;
}

export function ExportMenu({ guid }: ExportMenuProps): ReactElement {
  const { t } = useTranslation();
  const [format, setFormat] = useState<ExportFormat | null>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 text-xs">
            <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {t('export.menu.trigger')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              // Opens the standalone print view in a new tab; the user exports via
              // the browser's Save-as-PDF from there (PTV2-1253).
              window.open(`/patients/${guid}/print`, '_blank', 'noopener');
            }}
          >
            <Printer className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('export.menu.pdf')}
          </DropdownMenuItem>
          {/* FHIR and Phenopacket exports hidden until both endpoints work.
          <DropdownMenuItem
            onSelect={() => {
              setFormat('fhir');
            }}
          >
            <FileJson className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('export.menu.fhir')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              setFormat('phenopacket');
            }}
          >
            <FileJson className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('export.menu.phenopacket')}
          </DropdownMenuItem>
          */}
        </DropdownMenuContent>
      </DropdownMenu>
      {format !== null && (
        <ExportPreviewModal
          guid={guid}
          format={format}
          onClose={() => {
            setFormat(null);
          }}
        />
      )}
    </>
  );
}

interface ExportPreviewModalProps {
  readonly guid: string;
  readonly format: ExportFormat;
  readonly onClose: () => void;
}

function ExportPreviewModal({
  guid,
  format,
  onClose,
}: ExportPreviewModalProps): ReactElement {
  const { t } = useTranslation();

  const query = useQuery<Record<string, unknown>>({
    queryKey: ['person', guid, 'export', format],
    queryFn: async ({ signal }) =>
      format === 'fhir'
        ? apiClient.exportFhir(guid, { signal })
        : apiClient.exportPhenopacket(guid, { signal }),
    staleTime: 0,
    gcTime: 0,
  });

  const title =
    format === 'fhir'
      ? t('export.modal.fhirTitle')
      : t('export.modal.phenopacketTitle');

  const handleDownload = (): void => {
    if (query.data === undefined) return;
    const filename =
      format === 'fhir' ? `${guid}-fhir.json` : `${guid}-phenopacket.json`;
    downloadJson(filename, query.data);
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('export.modal.description')}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[420px] overflow-auto rounded-md border border-border bg-muted/40">
          {query.isPending ? (
            <div className="p-4">
              <MultilineTextSkeleton ariaLabel={t('export.modal.loading')} />
            </div>
          ) : query.isError ? (
            <div className="p-4">
              <FetchErrorCallout
                error={query.error}
                heading={t('export.modal.errorHeading')}
                message={t('export.modal.errorMessage')}
                codeLabel={t('routes.patient.error.codeLabel')}
                headingAs="p"
              />
            </div>
          ) : (
            <pre className="text-xs leading-relaxed font-mono p-4 whitespace-pre-wrap break-words">
              {JSON.stringify(query.data, null, 2)}
            </pre>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('export.modal.close')}
          </Button>
          <Button onClick={handleDownload} disabled={!query.isSuccess}>
            <Download className="h-4 w-4 mr-1.5" aria-hidden="true" />
            {t('export.modal.download')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
